import { fetchRaw } from '@papan/api-client'
import { iconCacheKey } from '@papan/types'
import type { IconAssetUrlResolver } from '@papan/core-ui/ui'

/**
 * Main-SPA resolver for a mini-app's server-composited icon PNG. The `-api` origin
 * is cross-site, so a plain `<img src>` wouldn't send the session cookie — we
 * fetch WITH credentials (like every other API call) and hand back an objectURL.
 * Memoized per `refId:rev:size` so a drive listing full of the same app's tiles
 * fetches once; a small LRU revokes evicted objectURLs to avoid leaks. Touch-on-hit
 * (below) keeps the CURRENTLY-visible icons at the young end, so eviction targets
 * the least-recently-requested entries first.
 *
 * KNOWN BOUND (accepted): eviction revokes an objectURL even if some `<img>` is
 * still pointing at it. With MAX=120 that needs >120 DISTINCT image-icon apps
 * requested more recently than a still-mounted one — implausible on any real
 * surface — and the failure mode is graceful: `MiniappIcon`'s `onError` drops to
 * the spec placeholder rather than a broken image. A refcounted store would remove
 * even that edge but isn't worth the machinery here.
 */
const MAX = 120
const cache = new Map<string, Promise<string | null>>()

async function fetchIconObjectUrl(refId: string, rev: string, size: number): Promise<string | null> {
  try {
    // `iconCacheKey`: the server's freshness check compares against the same key,
    // which moves when the server's renderer changes (#1015) — not only the rev.
    const res = await fetchRaw(`/api/miniapp/${encodeURIComponent(refId)}/icon.png?size=${size}&v=${encodeURIComponent(iconCacheKey(rev))}`)
    if (!res.ok) return null
    return URL.createObjectURL(await res.blob())
  } catch {
    return null
  }
}

export const spaIconAssetResolver: IconAssetUrlResolver = {
  load(refId, rev, size) {
    const key = `${refId}:${rev}:${size}`
    let p = cache.get(key)
    if (p) {
      // Touch: re-insert so a Map re-order keeps this the youngest entry (a hit on
      // a visible icon must not leave it near the eviction end).
      cache.delete(key)
      cache.set(key, p)
    } else {
      p = fetchIconObjectUrl(refId, rev, size)
      cache.set(key, p)
      // Don't cache a FAILURE: a transient error (offline, a 503 during a rolling
      // restart) resolves to null, and a cached null would pin the placeholder
      // until the rev changed or the entry was evicted — it must retry on the next
      // request instead. Drop the key if it resolved null; a successful (non-null)
      // objectURL stays cached. Guard on identity so a later same-key refetch that
      // already replaced this entry isn't clobbered.
      void p.then((u) => { if (!u && cache.get(key) === p) cache.delete(key) }).catch(() => { if (cache.get(key) === p) cache.delete(key) })
      if (cache.size > MAX) {
        const oldestKey = cache.keys().next().value
        if (oldestKey !== undefined && oldestKey !== key) {
          const old = cache.get(oldestKey)
          cache.delete(oldestKey)
          void old?.then((u) => { if (u) URL.revokeObjectURL(u) }).catch(() => {})
        }
      }
    }
    return p
  },
}
